import { Api } from "@/api/Api";
import { type VoidAsyncResult, createPendingAsyncResult } from "@/cache/AsyncResult";
import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import { createRetryState } from "@/cache/SpatioTemporalTileCache/RetryState";
import { TileGeometry } from "@/cache/SpatioTemporalTileCache/TileGeometry";
import { getCustomColormapRevision } from "@/cache/customColormapRevision";
import { ReservedValues } from "@/constants/reservedValues";
import type { ScenePublicApi } from "@/layers/Compositor";
import {
  getCurrentTileLayerTransitionRuntime,
  shouldAllowLowQualityFallbackTiles,
  shouldCheckTileLayerTransitionSettled,
  shouldDisableTileLayerTransitions,
  syncTileLayerTransitionState,
} from "@/layers/png/tileLayerTransitionPolicy";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { getModelBoundingBox } from "@/weather-parameters/lookup";
import {
  type CoordinateSystem,
  type GridRequest,
  GridSamplingStrategyKind,
  type ParameterUnit,
} from "@mm/api.meteomatics.com";
import type { WmsLayer } from "@mm/metx-workbench.meteomatics.com";
import { isEqual } from "lodash";
import type { DateTime } from "luxon";
import type { CustomSourceInterface, Map as MapboxMap } from "mapbox-gl";
import { customColormapToSamples } from "./utils";

type TilePosition = { x: number; y: number; z: number };

type TileEntry = {
  status: "pending" | "fulfilled" | "failed";
  promise: Promise<ImageBitmap | ImageData>;
  value?: ImageBitmap | ImageData;
};

type AnimationFrameHandle = number | ReturnType<typeof globalThis.setTimeout>;

type SourceCacheLike = {
  _reloadTile?: (id: number, state: string) => void;
  _tiles?: Record<
    string,
    VisibleTileLike & {
      hasData?: () => boolean;
      tileID?: {
        overscaledZ?: number;
        canonical?: { x: number; y: number; z: number };
      };
    }
  >;
  getVisibleCoordinates?: () => Array<{
    key: number;
    overscaledZ: number;
    canonical?: { x: number; y: number; z: number };
  }>;
  getTile?: (tileID: unknown) => VisibleTileLike | undefined;
};

type VisibleTileLike = {
  state?: string;
  setTexture?: (img: ImageBitmap | ImageData | HTMLCanvasElement, painter: unknown) => void;
};

type VisibleTilePayload = ImageBitmap | ImageData | HTMLCanvasElement;

type CustomColorMapLut = {
  lowerBound: number;
  upperBound: number;
  samples: Uint8ClampedArray;
  sampleCount: number;
  isSegmented: boolean;
};

type TileRequestSnapshot = {
  model: WmsLayer["model"];
  parameterUnit: WmsLayer["parameter_unit"];
  ensSelect: string;
  calibrated: WmsLayer["calibrated"];
  verticalInterpolation: WmsLayer["vertical_interpolation"];
  colorMapName: string;
  initDate?: string;
  apiUrlSuffix?: string;
};

type DeclutteredGridRequest<C extends CoordinateSystem> = GridRequest<C> & {
  decluttered?: boolean;
};

const RESERVED_EPS = 0.001;
const TILE_ENTRY_PRUNE_INTERVAL_MS = 250;
const MAX_TILE_ENTRY_COUNT = 512;
const MAX_VISIBLE_TILE_PAYLOAD_COUNT = 256;
const BACKING_TILE_MAX_ZOOM = 3;
const FALLBACK_PRELOAD_TILE_BUFFER = 2;
const TILE_FADE_DURATION_MS = 250;
const TIME_TRANSITION_PRELOAD_TIMEOUT_MS = 3000;

function createEmptyTile(tileSize: number) {
  const canvas = document.createElement("canvas");
  canvas.width = tileSize;
  canvas.height = tileSize;
  const ctx = canvas.getContext("2d");
  return ctx?.getImageData(0, 0, tileSize, tileSize) ?? new ImageData(tileSize, tileSize);
}

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function isReservedValue(v: number) {
  for (const reserved of ReservedValues) {
    if (Math.abs(v - reserved) < RESERVED_EPS) {
      return true;
    }
  }
  return false;
}

// Lambert custom-colormap path:
// fetch binary scalar tiles and convert them to raster tiles on CPU,
// then let Mapbox raster pipeline perform projection clipping/masking.
// Tradeoff: this is projection-robust for Lambert edges, but uses more CPU than the GPU prepass/mainpass path.
export class BinaryLambertRasterSource implements CustomSourceInterface<ImageBitmap | ImageData> {
  id: string;
  type = "custom" as const;
  dataType = "raster" as const;
  tileSize?: number | undefined;

  map?: MapboxMap;
  scene: ScenePublicApi;
  props: WmsLayer;
  displayTimeWithOffset: DateTime;
  nextTime: DateTime;

  private emptyTile: ImageData;
  private tileEntries = new Map<string, TileEntry>();
  private customColorMapCacheId?: string;
  private customColorMapPromise?: Promise<CustomColorMapLut>;
  private lastPruneAtMs = 0;
  private pendingTimeTransition = false;
  private pendingZoomInTransition = false;
  private lastObservedTimeKey: string | null = null;
  private lastObservedViewKey: string | null = null;
  private lastObservedZoomValue: number | null = null;
  private watchedRefreshKeys = new Set<string>();
  private activeVisibleTileFadeHandles = new Map<string, AnimationFrameHandle>();
  private visibleTilePayloads = new Map<string, VisibleTilePayload>();
  private queuedVisibleTileReloads = new Map<string, TilePosition>();
  private visibleTileReloadQueued = false;
  private pendingDisplayTimeWithOffset: DateTime | null = null;
  private pendingDisplayTimeViewKey: string | null = null;
  private pendingTimePreloadGeneration = 0;
  private pendingTimePreloadPromise: Promise<void> | null = null;
  private tileRefreshRevision = 0;

  constructor(id: string, scene: ScenePublicApi, props: WmsLayer) {
    this.id = id;
    this.scene = scene;
    this.tileSize = 256;
    this.props = props;

    this.displayTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDisplayTimeWithOffset(),
      this.props.kind as LayerKindName,
    );
    this.nextTime = roundToNearestCacheableTime(this.scene.getNextTime(), this.props.kind as LayerKindName);

    this.emptyTile = createEmptyTile(this.tileSize);
  }

  beforeRender() {
    this.syncInteractionState();
    if (this.shouldAllowFallbackTiles()) {
      this.prefetchFallbackTiles(this.displayTimeWithOffset);
    }
  }

  getRasterFadeDuration() {
    return this.shouldDisableTransitions() ? 0 : TILE_FADE_DURATION_MS;
  }

  updateSource(props: WmsLayer) {
    const nextDisplayTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDisplayTimeWithOffset(),
      this.props.kind as LayerKindName,
    );
    const nextTime = roundToNearestCacheableTime(this.scene.getNextTime(), this.props.kind as LayerKindName);
    const propsEqual = isEqual(this.props, props);
    const displayTimeChanged = +this.displayTimeWithOffset !== +nextDisplayTimeWithOffset;
    const nextTimeChanged = +this.nextTime !== +nextTime;

    if (
      propsEqual &&
      !displayTimeChanged &&
      !nextTimeChanged &&
      (!this.pendingDisplayTimeWithOffset || +this.pendingDisplayTimeWithOffset === +nextDisplayTimeWithOffset)
    ) {
      this.refreshColorMapTilesIfNeeded();
      return;
    }

    if (
      propsEqual &&
      !displayTimeChanged &&
      nextTimeChanged &&
      (!this.pendingDisplayTimeWithOffset || +this.pendingDisplayTimeWithOffset === +nextDisplayTimeWithOffset)
    ) {
      this.nextTime = nextTime;
      return;
    }

    if (this.shouldDeferTimeSourceSwap(props, nextDisplayTimeWithOffset)) {
      this.props = props;
      this.nextTime = nextTime;
      if (this.pendingDisplayTimeWithOffset && +this.pendingDisplayTimeWithOffset === +nextDisplayTimeWithOffset) {
        return;
      }
      this.cancelAllVisibleTileFades();
      this.queuedVisibleTileReloads.clear();
      this.pendingDisplayTimeWithOffset = nextDisplayTimeWithOffset;
      this.pendingDisplayTimeViewKey = this.getViewKey();
      this.ensurePendingTimeTransitionPreload();
      return;
    }

    this.pendingDisplayTimeWithOffset = null;
    this.pendingDisplayTimeViewKey = null;
    this.pendingTimePreloadGeneration++;
    this.pendingTimePreloadPromise = null;
    this.invalidateVisibleTileRefreshState();
    this.props = props;
    this.displayTimeWithOffset = nextDisplayTimeWithOffset;
    this.nextTime = nextTime;
    this.tileEntries.clear();
    this.customColorMapCacheId = undefined;
    this.customColorMapPromise = undefined;
    this.pruneTileEntries(true);
    this.refreshWholeSource();
  }

  private refreshColorMapTilesIfNeeded() {
    const currentColorMapCacheId = this.getCurrentColormapCacheId((this.props.color_map || "").trim());
    if (!this.customColorMapCacheId || this.customColorMapCacheId === currentColorMapCacheId) {
      return;
    }

    this.invalidateVisibleTileRefreshState();
    this.tileEntries.clear();
    this.customColorMapCacheId = undefined;
    this.customColorMapPromise = undefined;
    this.refreshWholeSource();
  }

  isRebuffering(datetime: DateTime) {
    const dateTimeWithOffset = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(datetime),
      this.props.kind as LayerKindName,
    );
    return this.getLoadingState(dateTimeWithOffset);
  }

  private getLoadingState(dateTimeWithOffset: DateTime) {
    this.pruneTileEntries();
    for (const tile of this.getCoveringTiles()) {
      const entry = this.getOrCreateTileEntry(tile, dateTimeWithOffset);
      if (entry.status === "pending") {
        return true;
      }
    }
    return false;
  }

  async getTilePromiseList(dateTimeWithOffset: DateTime) {
    this.pruneTileEntries();
    await Promise.all(
      this.getCoveringTiles().map((tile) => this.getOrCreateTileEntry(tile, dateTimeWithOffset).promise),
    );
  }

  getTileLoadResult(dateTimeWithOffset: DateTime): VoidAsyncResult {
    return createPendingAsyncResult(this.getTilePromiseList(dateTimeWithOffset).then(() => undefined));
  }

  hasTile({ x, y, z }: TilePosition): boolean {
    const geometry = new TileGeometry([x, y, z]);
    const modelBounds = getModelBoundingBox(this.props.model);
    const geometryEpsg3857 = geometry.toEpsg3857();
    const intersection = modelBounds.contains(geometryEpsg3857.project(modelBounds.crs));
    return intersection.kind !== "OutOfBounds";
  }

  getCoveringTiles(): TilePosition[] {
    try {
      // Internal Mapbox custom source helper.
      // @ts-ignore
      const tiles = this.coveringTiles() as TilePosition[] | undefined;
      return Array.isArray(tiles) ? tiles : [];
    } catch {
      return [];
    }
  }

  onAdd(map: MapboxMap) {
    this.map = map;
  }

  async loadTile({ x, y, z }: TilePosition, _options: { signal: AbortSignal }): Promise<ImageBitmap | ImageData> {
    this.pruneTileEntries();
    const targetPosition = { x, y, z };
    const exactEntry = this.getOrCreateTileEntry(targetPosition, this.displayTimeWithOffset);

    if (exactEntry.status === "fulfilled" && exactEntry.value) {
      return exactEntry.value;
    }

    if (!this.shouldAllowFallbackTiles()) {
      return exactEntry.promise;
    }

    if (this.hasCompleteHigherQualityDescendantCoverage(targetPosition)) {
      this.watchResultForRefresh(
        this.createTileRequestKey(targetPosition, this.displayTimeWithOffset),
        exactEntry,
        targetPosition,
        this.displayTimeWithOffset,
      );
      return exactEntry.promise;
    }

    const fallbackAncestors = this.getFallbackAncestorPositions(targetPosition);
    for (const ancestor of fallbackAncestors) {
      const ancestorEntry = this.getOrCreateTileEntry(ancestor, this.displayTimeWithOffset);
      if (ancestorEntry.status !== "fulfilled" || !ancestorEntry.value || ancestorEntry.value === this.emptyTile) {
        continue;
      }

      const fallbackPayload = this.createFallbackTile(ancestorEntry.value, ancestor, targetPosition);
      if (exactEntry.status === "pending") {
        this.watchResultForRefresh(
          this.createTileRequestKey(targetPosition, this.displayTimeWithOffset),
          exactEntry,
          targetPosition,
          this.displayTimeWithOffset,
          fallbackPayload,
        );
      }
      return fallbackPayload;
    }

    if (this.shouldPreferFallbackForPendingTile(this.displayTimeWithOffset)) {
      const pendingFallback = this.getPendingFallbackTile(targetPosition, fallbackAncestors);
      if (pendingFallback) {
        return this.resolvePendingFallbackOrExact(targetPosition, pendingFallback, exactEntry);
      }
    }

    return exactEntry.promise;
  }

  private pruneTileEntries(force = false) {
    if (this.tileEntries.size === 0) {
      return;
    }

    const now = performance.now();
    if (!force && now - this.lastPruneAtMs < TILE_ENTRY_PRUNE_INTERVAL_MS) {
      return;
    }
    this.lastPruneAtMs = now;

    const retainedTiles = new Map<string, TilePosition>();
    const coveringTiles = this.getCoveringTiles();
    for (const tile of coveringTiles) {
      retainedTiles.set(this.tileToDebugString(tile), tile);
    }
    if (this.shouldAllowFallbackTiles()) {
      for (const bufferedTile of this.getBufferedFallbackPreloadTiles()) {
        retainedTiles.set(this.tileToDebugString(bufferedTile), bufferedTile);
        for (const ancestor of this.getFallbackAncestorPositions(bufferedTile)) {
          retainedTiles.set(this.tileToDebugString(ancestor), ancestor);
        }
      }
    }

    if (retainedTiles.size > 0) {
      const visiblePrefixes = new Set([...retainedTiles.values()].map((tile) => `${tile.x}|${tile.y}|${tile.z}|`));
      for (const key of this.tileEntries.keys()) {
        const first = key.indexOf("|");
        const second = key.indexOf("|", first + 1);
        const third = key.indexOf("|", second + 1);
        if (third <= 0) {
          continue;
        }
        const tilePrefix = key.slice(0, third + 1);
        if (!visiblePrefixes.has(tilePrefix)) {
          this.tileEntries.delete(key);
        }
      }
    }

    // Safety net: keep bounded memory even under very active navigation.
    if (this.tileEntries.size <= MAX_TILE_ENTRY_COUNT) {
      return;
    }
    const removeCount = this.tileEntries.size - MAX_TILE_ENTRY_COUNT;
    let removed = 0;
    for (const key of this.tileEntries.keys()) {
      this.tileEntries.delete(key);
      removed++;
      if (removed >= removeCount) {
        break;
      }
    }
  }

  private getFallbackAncestorPositions({ x, y, z }: TilePosition): TilePosition[] {
    const positions: TilePosition[] = [];
    const maxAncestorZoom = Math.min(BACKING_TILE_MAX_ZOOM, z - 1);

    for (let ancestorZ = maxAncestorZoom; ancestorZ >= 0; ancestorZ--) {
      const scale = 2 ** (z - ancestorZ);
      positions.push({
        x: Math.floor(x / scale),
        y: Math.floor(y / scale),
        z: ancestorZ,
      });
    }

    return positions;
  }

  private prefetchFallbackTiles(dateTimeWithOffset: DateTime) {
    const prefetchedAncestorKeys = new Set<string>();

    for (const tile of this.getBufferedFallbackPreloadTiles()) {
      for (const ancestor of this.getFallbackAncestorPositions(tile)) {
        const ancestorKey = this.tileToDebugString(ancestor);
        if (prefetchedAncestorKeys.has(ancestorKey)) {
          continue;
        }
        prefetchedAncestorKeys.add(ancestorKey);
        this.getOrCreateTileEntry(ancestor, dateTimeWithOffset);
      }
    }
  }

  private getBufferedFallbackPreloadTiles(): TilePosition[] {
    const bufferedTiles = new Map<string, TilePosition>();

    for (const tile of this.getCoveringTiles()) {
      const maxCoordinate = 2 ** tile.z;
      for (let offsetX = -FALLBACK_PRELOAD_TILE_BUFFER; offsetX <= FALLBACK_PRELOAD_TILE_BUFFER; offsetX++) {
        for (let offsetY = -FALLBACK_PRELOAD_TILE_BUFFER; offsetY <= FALLBACK_PRELOAD_TILE_BUFFER; offsetY++) {
          const wrappedX = (((tile.x + offsetX) % maxCoordinate) + maxCoordinate) % maxCoordinate;
          const y = tile.y + offsetY;
          if (y < 0 || y >= maxCoordinate) {
            continue;
          }

          const bufferedTile = { x: wrappedX, y, z: tile.z };
          bufferedTiles.set(this.tileToDebugString(bufferedTile), bufferedTile);
        }
      }
    }

    return [...bufferedTiles.values()];
  }

  private getPendingFallbackTile(targetPosition: TilePosition, fallbackAncestors: TilePosition[]) {
    for (const ancestor of fallbackAncestors) {
      const ancestorEntry = this.getOrCreateTileEntry(ancestor, this.displayTimeWithOffset);
      if (ancestorEntry.status !== "pending") {
        continue;
      }

      return { ancestor, entry: ancestorEntry };
    }

    return undefined;
  }

  private async resolvePendingFallbackOrExact(
    targetPosition: TilePosition,
    pendingFallback: { ancestor: TilePosition; entry: TileEntry },
    exactEntry: TileEntry,
  ): Promise<ImageBitmap | ImageData> {
    return new Promise((resolve) => {
      let settled = false;

      pendingFallback.entry.promise
        .then((fallbackValue) => {
          if (settled || fallbackValue === this.emptyTile) {
            return;
          }

          settled = true;
          const fallbackPayload = this.createFallbackTile(fallbackValue, pendingFallback.ancestor, targetPosition);
          if (exactEntry.status === "pending") {
            this.watchResultForRefresh(
              this.createTileRequestKey(targetPosition, this.displayTimeWithOffset),
              exactEntry,
              targetPosition,
              this.displayTimeWithOffset,
              fallbackPayload,
            );
          }
          resolve(fallbackPayload);
        })
        .catch(() => undefined);

      exactEntry.promise
        .then((exactValue) => {
          if (settled) {
            return;
          }

          settled = true;
          resolve(exactValue);
        })
        .catch(() => {
          if (settled) {
            return;
          }

          settled = true;
          resolve(this.emptyTile);
        });
    });
  }

  private hasLoadedVisibleTile(dateTimeWithOffset: DateTime) {
    for (const tile of this.getCoveringTiles()) {
      const exactTile = this.getOrCreateTileEntry(tile, dateTimeWithOffset);
      if (exactTile.status === "fulfilled" && exactTile.value && exactTile.value !== this.emptyTile) {
        return true;
      }

      for (const ancestor of this.getFallbackAncestorPositions(tile)) {
        const fallbackTile = this.getOrCreateTileEntry(ancestor, dateTimeWithOffset);
        if (fallbackTile.status === "fulfilled" && fallbackTile.value && fallbackTile.value !== this.emptyTile) {
          return true;
        }
      }
    }

    return false;
  }

  private shouldPreferFallbackForPendingTile(dateTimeWithOffset: DateTime) {
    const isPanning = this.map?.isMoving?.() && !this.map?.isZooming?.() && !this.map?.isRotating?.();
    return isPanning || !this.hasLoadedVisibleTile(dateTimeWithOffset);
  }

  private createTileRequestKey({ x, y, z }: TilePosition, dateTimeWithOffset: DateTime) {
    return `${this.getTimeKey(dateTimeWithOffset)}:${z}:${x}:${y}`;
  }

  private watchResultForRefresh(
    key: string,
    entry: TileEntry,
    targetPosition: TilePosition,
    dateTimeWithOffset: DateTime,
    fallbackPayload?: ImageBitmap | ImageData,
  ) {
    const revision = this.tileRefreshRevision;
    const watchKey = `${revision}:${key}`;
    if (this.watchedRefreshKeys.has(watchKey)) {
      return;
    }
    this.watchedRefreshKeys.add(watchKey);

    entry.promise
      .then((payload) => {
        this.watchedRefreshKeys.delete(watchKey);
        if (!this.shouldRefreshResolvedTile(targetPosition, dateTimeWithOffset, revision)) {
          return;
        }

        if (fallbackPayload && payload !== this.emptyTile) {
          if (!this.shouldAnimateVisibleTileFade()) {
            this.cancelVisibleTileFade(this.createTileRequestKey(targetPosition, dateTimeWithOffset));
            this.setVisibleTilePayload(targetPosition, payload);
            this.scene.repaint();
            return;
          }
          this.fadeVisibleTileToPayload(targetPosition, dateTimeWithOffset, fallbackPayload, payload);
          return;
        }

        if (payload !== this.emptyTile) {
          this.setVisibleTilePayload(targetPosition, payload);
          this.scene.repaint();
          return;
        }

        this.queueVisibleTileReload(targetPosition);
      })
      .catch(() => {
        this.watchedRefreshKeys.delete(watchKey);
      });
  }

  private queueVisibleTileReload(targetPosition: TilePosition) {
    const key = this.createTileRequestKey(targetPosition, this.displayTimeWithOffset);
    this.queuedVisibleTileReloads.set(key, targetPosition);
    if (this.visibleTileReloadQueued) {
      return;
    }
    this.visibleTileReloadQueued = true;

    Promise.resolve().then(() => {
      this.visibleTileReloadQueued = false;
      if (this.hasPendingDisplayTimeTransition()) {
        this.queuedVisibleTileReloads.clear();
        return;
      }

      const pendingTileReloads = [...this.queuedVisibleTileReloads.values()];
      this.queuedVisibleTileReloads.clear();

      let reloadedVisibleTile = false;
      for (const tilePosition of pendingTileReloads) {
        reloadedVisibleTile = this.reloadVisibleTile(tilePosition) || reloadedVisibleTile;
      }

      if (reloadedVisibleTile) {
        this.scene.repaint();
      }
    });
  }

  private getVisibleTiles(targetPosition: TilePosition): VisibleTileLike[] {
    const visibleTiles: VisibleTileLike[] = [];
    const visibleTileSet = new Set<VisibleTileLike>();

    for (const sourceCache of this.getSourceCaches()) {
      const visibleCoordinates = sourceCache.getVisibleCoordinates?.() ?? [];
      for (const visibleCoordinate of visibleCoordinates) {
        if (!this.tileIdMatchesPosition(visibleCoordinate, targetPosition)) {
          continue;
        }

        const tile = sourceCache.getTile?.(visibleCoordinate);
        if (tile?.setTexture && !visibleTileSet.has(tile)) {
          visibleTileSet.add(tile);
          visibleTiles.push(tile);
        }
      }

      for (const tile of Object.values(sourceCache._tiles ?? {})) {
        if (!tile.setTexture || !this.tileIdMatchesPosition(tile.tileID, targetPosition) || visibleTileSet.has(tile)) {
          continue;
        }

        visibleTileSet.add(tile);
        visibleTiles.push(tile);
      }
    }

    return visibleTiles;
  }

  private hasCompleteHigherQualityDescendantCoverage(targetPosition: TilePosition) {
    const coveredTileKeys = new Set<string>();
    let maxCoveredZoom = targetPosition.z;

    for (const sourceCache of this.getSourceCaches()) {
      for (const tile of Object.values(sourceCache._tiles ?? {})) {
        const tilePosition = this.tileIdToPosition(tile.tileID);
        if (
          tilePosition &&
          this.isLoadedMapboxTile(tile) &&
          this.tilePositionIsDescendantOfPosition(tilePosition, targetPosition)
        ) {
          coveredTileKeys.add(this.tileToDebugString(tilePosition));
          maxCoveredZoom = Math.max(maxCoveredZoom, tilePosition.z);
        }
      }
    }

    for (let zoom = maxCoveredZoom - 1; zoom > targetPosition.z; zoom--) {
      const parentKeys = new Set<string>();
      for (const key of coveredTileKeys) {
        const tile = this.debugStringToTile(key);
        if (!tile || tile.z !== zoom + 1) {
          continue;
        }
        parentKeys.add(this.tileToDebugString({ x: Math.floor(tile.x / 2), y: Math.floor(tile.y / 2), z: zoom }));
      }

      for (const parentKey of parentKeys) {
        const parent = this.debugStringToTile(parentKey);
        if (parent && this.areImmediateChildrenCovered(parent, coveredTileKeys)) {
          coveredTileKeys.add(parentKey);
        }
      }
    }

    return this.areImmediateChildrenCovered(targetPosition, coveredTileKeys);
  }

  private isLoadedMapboxTile(tile: { state?: string; hasData?: () => boolean }) {
    return tile.hasData?.() ?? ["loaded", "reloading", "expired"].includes(tile.state ?? "");
  }

  private setVisibleTilePayload(targetPosition: TilePosition, payload: ImageBitmap | ImageData | HTMLCanvasElement) {
    const painter = (this.map as MapboxMap & { painter?: unknown })?.painter;
    if (!painter) {
      return false;
    }

    let updatedVisibleTile = false;
    for (const tile of this.getVisibleTiles(targetPosition)) {
      tile.setTexture?.(payload, painter);
      updatedVisibleTile = true;
    }

    if (updatedVisibleTile) {
      this.rememberVisibleTilePayload(targetPosition, payload);
    }

    return updatedVisibleTile;
  }

  private cancelVisibleTileFade(key: string) {
    const handle = this.activeVisibleTileFadeHandles.get(key);
    if (handle == null) {
      return;
    }

    this.activeVisibleTileFadeHandles.delete(key);
    if (typeof handle === "number" && typeof globalThis.cancelAnimationFrame === "function") {
      globalThis.cancelAnimationFrame(handle);
      return;
    }

    globalThis.clearTimeout(handle);
  }

  private cancelAllVisibleTileFades() {
    const fadeKeys = [...this.activeVisibleTileFadeHandles.keys()];
    for (const key of fadeKeys) {
      this.cancelVisibleTileFade(key);
    }
  }

  private invalidateVisibleTileRefreshState() {
    this.tileRefreshRevision++;
    this.watchedRefreshKeys.clear();
    this.queuedVisibleTileReloads.clear();
    this.visibleTilePayloads.clear();
    this.cancelAllVisibleTileFades();
  }

  private requestAnimationFrame(callback: () => void) {
    if (typeof globalThis.requestAnimationFrame === "function") {
      return globalThis.requestAnimationFrame(callback);
    }

    return globalThis.setTimeout(callback, 16);
  }

  private shouldAnimateVisibleTileFade() {
    return !this.shouldDisableTransitions() && !this.pendingZoomInTransition && !this.map?.isMoving?.();
  }

  private fadeVisibleTileToPayload(
    targetPosition: TilePosition,
    dateTimeWithOffset: DateTime,
    fromPayload: VisibleTilePayload,
    toPayload: VisibleTilePayload,
  ) {
    const key = this.createTileRequestKey(targetPosition, dateTimeWithOffset);
    const revision = this.tileRefreshRevision;
    this.cancelVisibleTileFade(key);

    const fadeDuration = this.getRasterFadeDuration();
    if (fadeDuration <= 0) {
      if (this.setVisibleTilePayload(targetPosition, toPayload)) {
        this.scene.repaint();
      }
      return;
    }

    const fromSource = this.toCanvasSource(fromPayload);
    const toSource = this.toCanvasSource(toPayload);
    if (!fromSource || !toSource) {
      if (this.setVisibleTilePayload(targetPosition, toPayload)) {
        this.scene.repaint();
      }
      return;
    }

    const blendedCanvas = document.createElement("canvas");
    blendedCanvas.width = this.tileSize ?? 256;
    blendedCanvas.height = this.tileSize ?? 256;
    const blendedContext = blendedCanvas.getContext("2d");
    if (!blendedContext) {
      if (this.setVisibleTilePayload(targetPosition, toPayload)) {
        this.scene.repaint();
      }
      return;
    }

    const startedAt = Date.now();
    const step = () => {
      if (!this.shouldRefreshResolvedTile(targetPosition, dateTimeWithOffset, revision)) {
        this.activeVisibleTileFadeHandles.delete(key);
        return;
      }

      if (!this.shouldAnimateVisibleTileFade()) {
        this.activeVisibleTileFadeHandles.delete(key);
        this.setVisibleTilePayload(targetPosition, toPayload);
        this.scene.repaint();
        return;
      }

      const progress = Math.min(1, (Date.now() - startedAt) / fadeDuration);
      blendedContext.clearRect(0, 0, blendedCanvas.width, blendedCanvas.height);
      blendedContext.globalAlpha = 1;
      blendedContext.drawImage(fromSource, 0, 0, blendedCanvas.width, blendedCanvas.height);
      blendedContext.globalAlpha = progress;
      blendedContext.drawImage(toSource, 0, 0, blendedCanvas.width, blendedCanvas.height);
      blendedContext.globalAlpha = 1;

      const visibleTileUpdated = this.setVisibleTilePayload(targetPosition, blendedCanvas);
      if (!visibleTileUpdated) {
        this.activeVisibleTileFadeHandles.delete(key);
        return;
      }

      this.scene.repaint();
      if (progress >= 1) {
        this.activeVisibleTileFadeHandles.delete(key);
        this.setVisibleTilePayload(targetPosition, toPayload);
        this.scene.repaint();
        return;
      }

      const nextHandle = this.requestAnimationFrame(step);
      this.activeVisibleTileFadeHandles.set(key, nextHandle);
    };

    const handle = this.requestAnimationFrame(step);
    this.activeVisibleTileFadeHandles.set(key, handle);
  }

  private getTileCacheKey(
    tile: TilePosition,
    dateTimeWithOffset: DateTime,
    colormapCacheId: string,
    requestSnapshot: TileRequestSnapshot,
  ) {
    return [
      tile.x,
      tile.y,
      tile.z,
      dateTimeWithOffset.toISO(),
      requestSnapshot.model,
      requestSnapshot.parameterUnit,
      requestSnapshot.ensSelect,
      requestSnapshot.calibrated ? "cal" : "raw",
      requestSnapshot.verticalInterpolation || "",
      requestSnapshot.initDate || "",
      requestSnapshot.apiUrlSuffix || "",
      colormapCacheId,
    ].join("|");
  }

  private getCurrentColormapCacheId(colorMapName: string) {
    const revision = getCustomColormapRevision(colorMapName);
    return `${colorMapName}@${revision}`;
  }

  private createRequestSnapshot(): TileRequestSnapshot {
    const customOptions = this.props.custom_options as { api_url_suffix?: string } | undefined;
    const apiUrlSuffix =
      typeof customOptions?.api_url_suffix === "string" && customOptions.api_url_suffix.trim() !== ""
        ? customOptions.api_url_suffix.trim()
        : undefined;

    return {
      model: this.props.model,
      parameterUnit: this.props.parameter_unit,
      ensSelect: this.props.ens_select || "",
      calibrated: this.props.calibrated,
      verticalInterpolation: this.props.vertical_interpolation,
      colorMapName: (this.props.color_map || "").trim(),
      initDate:
        typeof this.props.custom_options?.init_date === "string" && this.props.custom_options.init_date.trim() !== ""
          ? this.props.custom_options.init_date.trim()
          : undefined,
      apiUrlSuffix,
    };
  }

  private getOrCreateTileEntry(tile: TilePosition, dateTimeWithOffset: DateTime): TileEntry {
    if (!this.hasTile(tile)) {
      return {
        status: "fulfilled",
        value: this.emptyTile,
        promise: Promise.resolve(this.emptyTile),
      };
    }

    const requestSnapshot = this.createRequestSnapshot();
    const colormapCacheId = this.getCurrentColormapCacheId(requestSnapshot.colorMapName);
    const key = this.getTileCacheKey(tile, dateTimeWithOffset, colormapCacheId, requestSnapshot);
    const existing = this.tileEntries.get(key);
    if (existing) {
      return existing;
    }

    const entry: TileEntry = {
      status: "pending",
      promise: this.loadTileData(tile, dateTimeWithOffset, colormapCacheId, requestSnapshot)
        .then((result) => {
          entry.status = "fulfilled";
          entry.value = result;
          return result;
        })
        .catch(() => {
          entry.status = "failed";
          entry.value = this.emptyTile;
          return this.emptyTile;
        }),
    };

    this.tileEntries.set(key, entry);
    return entry;
  }

  private async loadTileData(
    tile: TilePosition,
    dateTimeWithOffset: DateTime,
    colormapCacheId: string,
    requestSnapshot: TileRequestSnapshot,
  ) {
    const lut = await this.getCustomColorMapLut(colormapCacheId, requestSnapshot.colorMapName);
    if (!lut) {
      return this.emptyTile;
    }

    const tileGeometry = new TileGeometry([tile.x, tile.y, tile.z]);
    const request: DeclutteredGridRequest<CoordinateSystem.EPSG3857> = {
      boundingBoxLimit: getModelBoundingBox(requestSnapshot.model),
      model: requestSnapshot.model,
      parameters: [requestSnapshot.parameterUnit as unknown as ParameterUnit],
      ensSelect: requestSnapshot.ensSelect || undefined,
      initDate: requestSnapshot.initDate,
      apiUrlSuffix: requestSnapshot.apiUrlSuffix,
      datetime: dateTimeWithOffset,
      area: tileGeometry.toEpsg3857(),
      sampling: { kind: GridSamplingStrategyKind.Area },
      width: this.tileSize ?? 256,
      height: this.tileSize ?? 256,
      calibrated: requestSnapshot.calibrated ?? undefined,
      vertical_interpolation: requestSnapshot.verticalInterpolation ?? undefined,
      decluttered: true,
    };

    const binaryTile = await apiThreadPool.getBinaryTile(tileGeometry, request, createRetryState());
    return this.applyColorMap(binaryTile.data.payload, binaryTile.data.width, binaryTile.data.height, lut);
  }

  private createFallbackTile(
    parentPayload: ImageBitmap | ImageData,
    parentPosition: TilePosition,
    targetPosition: TilePosition,
  ): ImageData | ImageBitmap {
    const canvas = document.createElement("canvas");
    canvas.width = this.tileSize ?? 256;
    canvas.height = this.tileSize ?? 256;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return this.emptyTile;
    }

    const sourceCanvas = this.toCanvasSource(parentPayload);
    if (!sourceCanvas) {
      return this.emptyTile;
    }

    const scale = 2 ** (targetPosition.z - parentPosition.z);
    const sourceWidth = parentPayload.width;
    const sourceHeight = parentPayload.height;
    const cropWidth = sourceWidth / scale;
    const cropHeight = sourceHeight / scale;
    const childX = targetPosition.x - parentPosition.x * scale;
    const childY = targetPosition.y - parentPosition.y * scale;

    ctx.imageSmoothingEnabled = true;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      sourceCanvas,
      childX * cropWidth,
      childY * cropHeight,
      cropWidth,
      cropHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    );

    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  }

  private toCanvasSource(payload: VisibleTilePayload): CanvasImageSource | null {
    if (payload instanceof ImageData) {
      const canvas = document.createElement("canvas");
      canvas.width = payload.width;
      canvas.height = payload.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return null;
      }
      ctx.putImageData(payload, 0, 0);
      return canvas;
    }

    return payload;
  }

  private syncInteractionState() {
    const timeKey = this.getTimeKey(this.displayTimeWithOffset);
    const viewKey = this.getViewKey();
    const currentZoom = this.scene.getMapViewport(0).zoom;
    const transitionState = {
      lastObservedTimeKey: this.lastObservedTimeKey,
      lastObservedViewKey: this.lastObservedViewKey,
      lastObservedZoomValue: this.lastObservedZoomValue,
      pendingTimeTransition: this.pendingTimeTransition,
      pendingZoomDirection: this.pendingZoomInTransition ? ("in" as const) : null,
    };
    const mayNeedSettledCheck = shouldCheckTileLayerTransitionSettled(
      transitionState,
      { timeKey, viewKey, zoom: currentZoom },
      { trackZoomOut: false },
    );

    syncTileLayerTransitionState(transitionState, {
      timeKey,
      viewKey,
      zoom: currentZoom,
      runtime: getCurrentTileLayerTransitionRuntime(this.map),
      isSettled: mayNeedSettledCheck ? !this.getLoadingState(this.displayTimeWithOffset) : false,
    });

    this.lastObservedTimeKey = transitionState.lastObservedTimeKey;
    this.lastObservedViewKey = transitionState.lastObservedViewKey;
    this.lastObservedZoomValue = transitionState.lastObservedZoomValue;
    this.pendingTimeTransition = transitionState.pendingTimeTransition;
    this.pendingZoomInTransition = transitionState.pendingZoomDirection === "in";
  }

  private shouldDisableTransitions() {
    return shouldDisableTileLayerTransitions(getCurrentTileLayerTransitionRuntime(this.map));
  }

  private shouldAllowFallbackTiles() {
    return shouldAllowLowQualityFallbackTiles(getCurrentTileLayerTransitionRuntime(this.map), {
      pendingTimeTransition: this.pendingTimeTransition,
      pendingZoomDirection: this.pendingZoomInTransition ? "in" : null,
    });
  }

  private shouldDeferTimeSourceSwap(props: WmsLayer, nextDisplayTimeWithOffset: DateTime) {
    if (this.shouldDisableTransitions() || !isEqual(this.props, props)) {
      return false;
    }

    if (+this.displayTimeWithOffset === +nextDisplayTimeWithOffset) {
      return false;
    }

    if (this.map?.isMoving?.()) {
      return false;
    }

    return this.lastObservedViewKey === this.getViewKey();
  }

  private getTimeKey(dateTimeWithOffset: DateTime) {
    return dateTimeWithOffset.toUTC().toISO({ suppressMilliseconds: true }) ?? "";
  }

  private getViewKey() {
    const viewport = this.scene.getMapViewport(0);
    const area = viewport.viewportArea;
    return `${viewport.zoom}|${area.xyMin[0]}|${area.xyMin[1]}|${area.xyMax[0]}|${area.xyMax[1]}|${area.z}`;
  }

  private hasPendingDisplayTimeTransition() {
    return (
      this.pendingDisplayTimeWithOffset != null &&
      this.getTimeKey(this.pendingDisplayTimeWithOffset) !== this.getTimeKey(this.displayTimeWithOffset)
    );
  }

  private isTileCurrentlyVisible(targetPosition: TilePosition) {
    return this.getCoveringTiles().some(
      (tile) => tile.x === targetPosition.x && tile.y === targetPosition.y && tile.z === targetPosition.z,
    );
  }

  private shouldRefreshResolvedTile(targetPosition: TilePosition, dateTimeWithOffset: DateTime, revision: number) {
    if (revision !== this.tileRefreshRevision) {
      return false;
    }

    if (this.hasPendingDisplayTimeTransition()) {
      return false;
    }

    return (
      this.getTimeKey(this.displayTimeWithOffset) === this.getTimeKey(dateTimeWithOffset) &&
      this.isTileCurrentlyVisible(targetPosition)
    );
  }

  private tileToDebugString({ x, y, z }: TilePosition) {
    return `${z}/${x}/${y}`;
  }

  private tileIdMatchesPosition(
    tileID: { overscaledZ?: number; canonical?: { x: number; y: number; z: number } } | undefined,
    position: TilePosition,
  ) {
    return (
      (tileID?.overscaledZ ?? tileID?.canonical?.z) === position.z &&
      tileID?.canonical?.x === position.x &&
      tileID?.canonical?.y === position.y
    );
  }

  private tileIdToPosition(
    tileID: { overscaledZ?: number; canonical?: { x: number; y: number; z: number } } | undefined,
  ): TilePosition | null {
    const tileZ = tileID?.overscaledZ ?? tileID?.canonical?.z;
    if (!tileID?.canonical || tileZ == null) {
      return null;
    }

    return { x: tileID.canonical.x, y: tileID.canonical.y, z: tileZ };
  }

  private tilePositionIsDescendantOfPosition(tile: TilePosition, position: TilePosition) {
    if (tile.z <= position.z) {
      return false;
    }

    const scale = 2 ** (tile.z - position.z);
    return Math.floor(tile.x / scale) === position.x && Math.floor(tile.y / scale) === position.y;
  }

  private areImmediateChildrenCovered(position: TilePosition, coveredTileKeys: Set<string>) {
    const childZ = position.z + 1;
    return (
      coveredTileKeys.has(this.tileToDebugString({ x: position.x * 2, y: position.y * 2, z: childZ })) &&
      coveredTileKeys.has(this.tileToDebugString({ x: position.x * 2 + 1, y: position.y * 2, z: childZ })) &&
      coveredTileKeys.has(this.tileToDebugString({ x: position.x * 2, y: position.y * 2 + 1, z: childZ })) &&
      coveredTileKeys.has(this.tileToDebugString({ x: position.x * 2 + 1, y: position.y * 2 + 1, z: childZ }))
    );
  }

  private debugStringToTile(key: string): TilePosition | null {
    const [z, x, y] = key.split("/").map(Number);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      return null;
    }

    return { x, y, z };
  }

  private rememberVisibleTilePayload(targetPosition: TilePosition, payload: VisibleTilePayload) {
    const key = this.tileToDebugString(targetPosition);
    this.visibleTilePayloads.delete(key);
    this.visibleTilePayloads.set(key, payload);
    this.pruneVisibleTilePayloads();
  }

  private getRememberedVisibleTilePayload(targetPosition: TilePosition) {
    return this.visibleTilePayloads.get(this.tileToDebugString(targetPosition));
  }

  private pruneVisibleTilePayloads() {
    if (this.visibleTilePayloads.size === 0) {
      return;
    }

    const visibleTileKeys = new Set(this.getCoveringTiles().map((tile) => this.tileToDebugString(tile)));
    if (visibleTileKeys.size > 0) {
      for (const key of this.visibleTilePayloads.keys()) {
        if (!visibleTileKeys.has(key)) {
          this.visibleTilePayloads.delete(key);
        }
      }
    }

    if (this.visibleTilePayloads.size <= MAX_VISIBLE_TILE_PAYLOAD_COUNT) {
      return;
    }

    const removeCount = this.visibleTilePayloads.size - MAX_VISIBLE_TILE_PAYLOAD_COUNT;
    let removed = 0;
    for (const key of this.visibleTilePayloads.keys()) {
      this.visibleTilePayloads.delete(key);
      removed++;
      if (removed >= removeCount) {
        break;
      }
    }
  }

  private getSourceCaches(): SourceCacheLike[] {
    const style = (
      this.map as MapboxMap & {
        style?: {
          getSourceCaches?: (sourceId?: string | null) => unknown[];
        };
      }
    )?.style;

    return (style?.getSourceCaches?.(this.id) ?? []) as SourceCacheLike[];
  }

  private reloadVisibleTile(targetPosition: TilePosition) {
    let reloadedVisibleTile = false;

    for (const sourceCache of this.getSourceCaches()) {
      const visibleCoordinates = sourceCache.getVisibleCoordinates?.() ?? [];
      for (const visibleCoordinate of visibleCoordinates) {
        if (
          visibleCoordinate.overscaledZ !== targetPosition.z ||
          visibleCoordinate.canonical?.x !== targetPosition.x ||
          visibleCoordinate.canonical?.y !== targetPosition.y
        ) {
          continue;
        }

        const tile = sourceCache.getTile?.(visibleCoordinate);
        sourceCache._reloadTile?.(visibleCoordinate.key, tile?.state ?? "loaded");
        reloadedVisibleTile = true;
      }
    }

    return reloadedVisibleTile;
  }

  private getLoadedExactTilePayload(targetPosition: TilePosition, dateTimeWithOffset: DateTime) {
    const exactEntry = this.getOrCreateTileEntry(targetPosition, dateTimeWithOffset);
    if (exactEntry.status !== "fulfilled" || !exactEntry.value || exactEntry.value === this.emptyTile) {
      return undefined;
    }

    return exactEntry.value;
  }

  private transitionVisibleTileToPreloadedPayload(targetPosition: TilePosition, dateTimeWithOffset: DateTime) {
    const exactPayload = this.getLoadedExactTilePayload(targetPosition, dateTimeWithOffset);
    if (!exactPayload) {
      this.queueVisibleTileReload(targetPosition);
      return false;
    }

    const currentPayload = this.getRememberedVisibleTilePayload(targetPosition);
    if (currentPayload && this.shouldAnimateVisibleTileFade()) {
      this.fadeVisibleTileToPayload(targetPosition, dateTimeWithOffset, currentPayload, exactPayload);
      return true;
    }

    if (this.setVisibleTilePayload(targetPosition, exactPayload)) {
      this.scene.repaint();
      return true;
    }

    this.queueVisibleTileReload(targetPosition);
    return false;
  }

  private refreshWholeSource() {
    // @ts-ignore Mapbox custom source internal refresh hook.
    this.map?.getSource(this.id)?._update();
    this.scene.repaint();
  }

  private async preloadVisibleExactTiles(dateTimeWithOffset: DateTime) {
    const visibleTileRequests = this.getCoveringTiles().map((tile) =>
      this.getOrCreateTileEntry(tile, dateTimeWithOffset),
    );
    await Promise.allSettled(visibleTileRequests.map((entry) => entry.promise));
  }

  private ensurePendingTimeTransitionPreload() {
    if (!this.pendingDisplayTimeWithOffset || !this.pendingDisplayTimeViewKey) {
      return;
    }
    if (this.pendingTimePreloadPromise) {
      return;
    }

    const generation = ++this.pendingTimePreloadGeneration;
    const targetTime = this.pendingDisplayTimeWithOffset;
    const targetViewKey = this.pendingDisplayTimeViewKey;
    const timeoutHandle = globalThis.setTimeout(() => {
      if (!this.shouldApplyPendingTimeTransition(generation, targetTime, targetViewKey)) {
        return;
      }

      this.applyPendingTimeTransition(targetTime);
      this.pendingTimePreloadPromise = null;
      this.refreshWholeSource();
    }, TIME_TRANSITION_PRELOAD_TIMEOUT_MS);

    this.pendingTimePreloadPromise = this.preloadVisibleExactTiles(targetTime)
      .then(() => {
        globalThis.clearTimeout(timeoutHandle);
        if (!this.shouldApplyPendingTimeTransition(generation, targetTime, targetViewKey)) {
          return;
        }

        this.applyPendingTimeTransition(targetTime);
      })
      .finally(() => {
        globalThis.clearTimeout(timeoutHandle);
        if (generation === this.pendingTimePreloadGeneration) {
          this.pendingTimePreloadPromise = null;
        }
      });
  }

  private shouldApplyPendingTimeTransition(generation: number, targetTime: DateTime, targetViewKey: string) {
    return (
      generation === this.pendingTimePreloadGeneration &&
      !!this.pendingDisplayTimeWithOffset &&
      this.getTimeKey(this.pendingDisplayTimeWithOffset) === this.getTimeKey(targetTime) &&
      this.getViewKey() === targetViewKey
    );
  }

  private applyPendingTimeTransition(targetTime: DateTime) {
    this.pendingTimeTransition = true;
    this.displayTimeWithOffset = targetTime;
    this.nextTime = targetTime;
    this.pendingDisplayTimeWithOffset = null;
    this.pendingDisplayTimeViewKey = null;

    for (const tile of this.getCoveringTiles()) {
      this.transitionVisibleTileToPreloadedPayload(tile, targetTime);
    }
  }

  private async getCustomColorMapLut(
    colormapCacheId: string,
    colorMapName: string,
  ): Promise<CustomColorMapLut | undefined> {
    if (this.customColorMapCacheId !== colormapCacheId || !this.customColorMapPromise) {
      if (colorMapName === "") {
        return undefined;
      }

      this.customColorMapCacheId = colormapCacheId;
      this.customColorMapPromise = Api.colormaps.colormapsGet({ colormapId: colorMapName }).then((colormap) => {
        const { usualValueRange, samples } = customColormapToSamples(colormap);
        const packed = new Uint8ClampedArray(samples.length * 4);
        let out = 0;
        for (const sample of samples) {
          packed[out++] = Math.round(sample[0] * 255);
          packed[out++] = Math.round(sample[1] * 255);
          packed[out++] = Math.round(sample[2] * 255);
          packed[out++] = Math.round(sample[3] * 255);
        }

        return {
          lowerBound: usualValueRange.lowerBound,
          upperBound: usualValueRange.upperBound,
          samples: packed,
          sampleCount: packed.length / 4,
          isSegmented: String(colormap.type ?? "").toLowerCase() === "segmented",
        };
      });
    }

    try {
      const lut = await this.customColorMapPromise;
      if (this.customColorMapCacheId !== colormapCacheId) {
        return undefined;
      }
      return lut;
    } catch {
      return undefined;
    }
  }

  private applyColorMap(payload: Float32Array, width: number, height: number, lut: CustomColorMapLut): ImageData {
    const rgba = new Uint8ClampedArray(width * height * 4);
    const denominator = Math.max(lut.upperBound - lut.lowerBound, 1e-6);

    // Binary payload is vertically flipped relative to raster image convention.
    for (let y = 0; y < height; y++) {
      const srcY = height - 1 - y;
      const srcRowOffset = srcY * width;
      const dstRowOffset = y * width * 4;

      for (let x = 0; x < width; x++) {
        const value = payload[srcRowOffset + x];
        const dstOffset = dstRowOffset + x * 4;

        if (!Number.isFinite(value) || isReservedValue(value)) {
          rgba[dstOffset + 0] = 0;
          rgba[dstOffset + 1] = 0;
          rgba[dstOffset + 2] = 0;
          rgba[dstOffset + 3] = 0;
          continue;
        }

        const unit = clamp01((value - lut.lowerBound) / denominator);
        const [r, g, b, a] = this.sampleColorMap(lut, unit);
        rgba[dstOffset + 0] = r;
        rgba[dstOffset + 1] = g;
        rgba[dstOffset + 2] = b;
        rgba[dstOffset + 3] = a;
      }
    }

    return new ImageData(rgba, width, height);
  }

  private sampleColorMap(lut: CustomColorMapLut, unit: number): [number, number, number, number] {
    const maxIndex = Math.max(lut.sampleCount - 1, 0);
    if (lut.sampleCount <= 1) {
      return [lut.samples[0] ?? 0, lut.samples[1] ?? 0, lut.samples[2] ?? 0, lut.samples[3] ?? 255];
    }

    if (lut.isSegmented) {
      const idx = Math.max(0, Math.min(maxIndex, Math.round(unit * maxIndex)));
      const base = idx * 4;
      return [lut.samples[base], lut.samples[base + 1], lut.samples[base + 2], lut.samples[base + 3]];
    }

    const position = unit * maxIndex;
    const idx0 = Math.floor(position);
    const idx1 = Math.min(maxIndex, idx0 + 1);
    const t = position - idx0;
    const base0 = idx0 * 4;
    const base1 = idx1 * 4;

    return [
      Math.round(lerp(lut.samples[base0], lut.samples[base1], t)),
      Math.round(lerp(lut.samples[base0 + 1], lut.samples[base1 + 1], t)),
      Math.round(lerp(lut.samples[base0 + 2], lut.samples[base1 + 2], t)),
      Math.round(lerp(lut.samples[base0 + 3], lut.samples[base1 + 3], t)),
    ];
  }
}
